import {
  Annotation,
  EllipseAnnotation as EllipseAnnotationType,
  FreehandAnnotation as FreehandAnnotationType,
  HPFAnnotation as HPFAnnotationType,
  Indication,
  LineAnnotation as LineAnnotationType,
  MultilineAnnotation as MultiLineAnnotationType,
  PointAnnotation as PointAnnotationType,
  ProcessedImageWithAnnotations,
  TextAnnotation as TextAnnotationType,
  ZoomDefinition,
  MultilineAnnotationClass,
  MultilineCategory,
  ImageWithAnnotations,
  Fragment
} from "../models";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { TileLayer, useMap, useMapEvents } from "react-leaflet";
import {
  annotationsInBounds,
  annotationWeight,
  buildFragmentLookup,
  cachedMap,
  convertCoordinatesToLatLngLiteral,
  ElementCache,
  formatDate,
  formatMicrometers,
  FragmentLookup,
  indexAnnotationsByBoundingBox,
  isPointInPolygon,
  mapStateToFragments,
  toMicrometers
} from "../utils";

import EllipseAnnotationWithPopup from "../components/EllipseAnnotation";
import FreehandAnnotation from "../components/FreehandAnnotation";
import HPFAnnotation, { HPF_CURSOR } from "../components/HPFAnnotation";
import LineAnnotation from "../components/LineAnnotation";
import MultilineAnnotation from "../components/MultilineAnnotation";
import PointAnnotation, {
  defaultProps as pointAnnotationDefaults,
  HIGHLIGHT_RADIUS_MULTIPLIER
} from "../components/PointAnnotation";
import TextMarker from "../components/TextMarker";
import { checkLoggedIn } from "../slices/auth";
import { throttle } from "lodash";
import { range } from "ramda";
import {
  DrawMap,
  LatLngBounds,
  LatLngLiteral,
  LeafletEvent,
  LeafletEventHandlerFnMap
} from "leaflet";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  AnnotationType,
  SelectedAnnotationType,
  createHpfImageAnnotation
} from "../slices/caseImageViewer";
import { LoggedInUser, RolePermissions } from "../permissions";
import { Resource } from "../types";
import { AppDispatch } from "../store";

interface ImageMapProps {
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly allAnnotations: ReadonlyArray<Annotation>;
  readonly userCanDeleteAnnotations: boolean;
  readonly scaleFactor: number;
  readonly isAnnotationInProgress: boolean;
  readonly imageWithAnnotations: ImageWithAnnotations;
  readonly selectedAnnotation: SelectedAnnotationType | null;
  readonly tempHpf: LatLngLiteral | null;
  readonly hpfRadius: number;
  readonly zoom: number;
  readonly magnifications: ReadonlyArray<ZoomDefinition>;
  readonly isMicroscopeActive: boolean;
  readonly isSidebarExpanded: boolean;
  readonly resolutionInMeters: number;
  readonly tileUrl: string;
  readonly hpfCursorVisible: boolean;
  readonly hpfCursorColor: string | null;
  readonly showLineLabels: boolean;
  readonly spacebarDown: boolean;
  readonly annotationOpacity: number | null;
  readonly loggedInUser: Resource<LoggedInUser>;
  readonly enableAnnotationControlCaching: boolean;
}

interface AnnotationElementCaches {
  readonly point: ElementCache<JSX.Element>;
  readonly hpf: ElementCache<JSX.Element>;
  readonly freehand: ElementCache<JSX.Element>;
  readonly text: ElementCache<JSX.Element>;
  readonly ellipse: ElementCache<JSX.Element>;
  readonly line: ElementCache<JSX.Element>;
}

// Only ever read -- `cachedMap` returns fresh Maps -- hence the readonly cache types above.
const EMPTY_ELEMENT_CACHES: AnnotationElementCaches = {
  point: new Map(),
  hpf: new Map(),
  freehand: new Map(),
  text: new Map(),
  ellipse: new Map(),
  line: new Map()
};

// How far outside the viewport annotations are kept mounted, as a fraction of the viewport size on
// each side. Large enough that a normal drag does not expose un-mounted annotations before the
// `moveend` that re-evaluates the cull, and that the small on-map footprints Leaflet adds to a
// path (stroke width, marker icons) cannot straddle the edge unnoticed. Re-entering a previously
// culled annotation costs a real (if now much smaller) mount, not just a re-render -- React has no
// fiber for something that wasn't in the tree last commit, so caching its element doesn't help.
// 0.75 of a viewport on each side, so the padded box is ~7.6x the visible area: wide enough that
// routine panning rarely crosses the boundary at all. Raising this further reduces how often that
// mount cost is paid but shrinks culling's benefit at rest -- tune it against a measurement, not a
// hunch.
const VIEWPORT_CULL_PADDING = 0.75;

// If the (padded) viewport already holds at least this fraction of all annotations, skip culling
// entirely and mount everything instead of a near-total-but-not-quite subset.
//
// Two thresholds, not one: a single cutoff isn't hysteresis, it's just a line the viewport can
// straddle -- and it will, since panning near "nearly everything visible" is a normal thing to do.
// Measured live: a continuous drag oscillated 100% -> 75.4% -> 100% visible across a single
// threshold at 0.85, remounting thousands of annotations on every crossing. With separate
// enter/exit thresholds there is a dead zone in between where neither transition fires, so hovering
// anywhere in that range (however the viewport drifts) can't cause repeated remounts.
const HYSTERESIS_ENTER_RATIO = 0.9; // switch to "mount everything" once visible reaches this
const HYSTERESIS_EXIT_RATIO = 0.6; // only switch back to culling once visible drops below this

/**
 * The map's current viewport, padded, recomputed only when the map settles.
 *
 * Deliberately keyed off `moveend`/`zoomend` rather than `move`/`zoom`: re-culling on every frame
 * of a drag would replace the per-frame cost this is meant to remove.
 */
const useCullingBounds = (paddingRatio: number): LatLngBounds | null => {
  const map = useMap();
  const [bounds, setBounds] = useState<LatLngBounds | null>(null);

  const updateBounds = () => setBounds(map.getBounds().pad(paddingRatio));

  useMapEvents({
    moveend: updateBounds,
    zoomend: updateBounds,
    // Toggling the sidebar calls `invalidateSize`, which changes the viewport without a move.
    resize: updateBounds
  });

  useEffect(updateBounds, [map]);

  return bounds;
};

const ImageMap = ({
  minZoom,
  maxZoom,
  allAnnotations,
  userCanDeleteAnnotations,
  scaleFactor,
  isAnnotationInProgress,
  imageWithAnnotations,
  tempHpf,
  hpfRadius,
  isSidebarExpanded,
  resolutionInMeters,
  tileUrl,
  hpfCursorVisible,
  hpfCursorColor,
  showLineLabels,
  spacebarDown,
  annotationOpacity,
  loggedInUser,
  enableAnnotationControlCaching
}: ImageMapProps) => {
  const drawMap = useMap() as DrawMap;

  const dispatch = useAppDispatch();

  const processedImageWithAnnotations = imageWithAnnotations as ProcessedImageWithAnnotations;

  const topLevelFragments = useAppSelector(state => mapStateToFragments(state));
  const topLevelAnnotations = topLevelFragments;
  const topLevelAIAnnotations = topLevelFragments;

  const indications = useAppSelector(
    state => state.studyConfiguration.study.data.indications.value
  );

  const selectedMultilineClass = useAppSelector(
    state => state.caseImageViewer.selectedMultilineClass
  );

  const selectedMultilineCategory = useAppSelector(
    state => state.caseImageViewer.selectedMultilineCategory
  );

  const [hpfCursor, setHpfCursor] = useState({ latlng: null, hold: tempHpf });

  if (tempHpf === null && hpfCursor.hold !== null) {
    setTimeout(() => setHpfCursor({ ...hpfCursor, hold: null }), 170);
  }

  const eventHandlers: LeafletEventHandlerFnMap = {
    click: (event: LeafletEvent) => {
      const e = event as any;
      if (hpfCursorVisible && !spacebarDown) {
        const noop = () => 0;
        setHpfCursor({ ...hpfCursor, hold: hpfCursor.latlng });
        dispatch(
          createHpfImageAnnotation({ point: e.latlng, radius: hpfRadius, cleanupCallback: noop })
        );
      }
    },
    mousemove: (event: LeafletEvent) => {
      const e = event as any;
      if (hpfCursorVisible) {
        setHpfCursor({ ...hpfCursor, latlng: e.latlng });
      }
    }
  };

  const mapElement = useMapEvents(eventHandlers);

  // Persists across renders so an annotation whose rendered element hasn't changed keeps the same
  // element reference next time, letting React skip its subtree instead of reconciling it.
  const elementCachesRef = useRef<AnnotationElementCaches>(EMPTY_ELEMENT_CACHES);

  // Resolving each annotation's place in the fragment tree by scanning it is what made rebuilding
  // the annotation controls cost seconds; index it once per annotation set instead.
  const fragmentLookup = useMemo(() => buildFragmentLookup(topLevelAnnotations), [
    topLevelAnnotations
  ]);

  const aiFragmentLookup = useMemo(
    () =>
      topLevelAIAnnotations === topLevelAnnotations
        ? fragmentLookup
        : buildFragmentLookup(topLevelAIAnnotations),
    [fragmentLookup, topLevelAIAnnotations, topLevelAnnotations]
  );

  const cullingBounds = useCullingBounds(VIEWPORT_CULL_PADDING);

  // Annotations highlighted from the sidebar stay mounted wherever the viewport is, so selecting an
  // off-screen annotation still behaves as it did before culling.
  const highlightedAnnotationIds = useAppSelector(
    state => state.caseImageViewer.highlightedAnnotations
  );
  const alwaysVisibleAnnotationIds = useMemo(() => new Set(highlightedAnnotationIds), [
    highlightedAnnotationIds
  ]);

  // Largest on-map radius a point annotation can be drawn at, in metres, so a point near the edge
  // of the viewport is not culled while part of its (possibly sidebar-highlighted, larger) circle
  // would still be on screen.
  const pointRadiusInMeters =
    pointAnnotationDefaults.radius * scaleFactor * HIGHLIGHT_RADIUS_MULTIPLIER;

  // Bounding boxes are computed once per annotation set; only the (cheap) rectangle overlap test
  // below re-runs when the viewport changes.
  const indexedAnnotations = useMemo(
    () => indexAnnotationsByBoundingBox(allAnnotations, pointRadiusInMeters),
    [allAnnotations, pointRadiusInMeters]
  );

  // `useCullingBounds` hands back a fresh bounds object every time the map settles, so the cull
  // re-runs on every pan and zoom. Most of those produce exactly the same set of annotations -- a
  // short drag, or any move at all once the whole image fits on screen -- and rebuilding the
  // annotation controls for an unchanged set is pure waste. Hold on to the previous array so its
  // identity survives, which keeps `cachedAnnotations` below from recomputing.
  const previousVisibleAnnotations = useRef<ReadonlyArray<Annotation>>([]);

  // Which side of the hysteresis dead zone (see the two ratios above) the previous computation
  // landed on -- which threshold applies depends on this, not just the current ratio.
  const wasNearlyEverythingVisible = useRef(false);

  const visibleAnnotations = useMemo(() => {
    const culled = annotationsInBounds(
      indexedAnnotations,
      cullingBounds,
      alwaysVisibleAnnotationIds
    );
    const visibleRatio =
      indexedAnnotations.length > 0 ? culled.length / indexedAnnotations.length : 1;
    const threshold = wasNearlyEverythingVisible.current
      ? HYSTERESIS_EXIT_RATIO
      : HYSTERESIS_ENTER_RATIO;
    const nearlyEverythingVisible = visibleRatio >= threshold;
    // eslint-disable-next-line functional/immutable-data
    wasNearlyEverythingVisible.current = nearlyEverythingVisible;
    const candidate = nearlyEverythingVisible
      ? indexedAnnotations.map(indexed => indexed.annotation)
      : culled;
    const previous = previousVisibleAnnotations.current;
    const unchanged =
      candidate.length === previous.length &&
      candidate.every((annotation, index) => annotation === previous[index]);
    // eslint-disable-next-line functional/immutable-data
    previousVisibleAnnotations.current = unchanged ? previous : candidate;
    return previousVisibleAnnotations.current;
  }, [indexedAnnotations, cullingBounds, alwaysVisibleAnnotationIds]);

  useEffect(() => {
    // Collapsing/expanding the sidebar changes the map size. When this happens, we need to tell
    // Leaflet to update the map so tiles load properly.
    //
    mapElement.invalidateSize();
  }, [isSidebarExpanded]);

  const { pixelSize, zStackSize } = processedImageWithAnnotations.imageAndQuery.image;

  const zStackTileUrls =
    zStackSize && zStackSize > 1
      ? range(1, zStackSize).map(
          (index: number) => [index, tileUrl + `?zStackIndex=${index}`] as readonly [number, string]
        )
      : [];

  useEffect(() => {
    const selectedAnnotation = cachedAnnotations.multiLineAnnotations.find(
      annotation =>
        selectedMultilineClass &&
        annotation.annotationClassId === selectedMultilineClass.id &&
        annotation.multilineCategory === selectedMultilineCategory
    );
    if (!selectedAnnotation) {
      mapElement.closePopup();
    }
  }, [selectedMultilineClass, selectedMultilineCategory]);

  const cachedAnnotations = enableAnnotationControlCaching
    ? useMemo(() => {
        const { controls, caches } = loadAnnotationControls(
          dispatch,
          visibleAnnotations,
          allAnnotations,
          alwaysVisibleAnnotationIds,
          fragmentLookup,
          aiFragmentLookup,
          userCanDeleteAnnotations,
          scaleFactor,
          annotationOpacity,
          isAnnotationInProgress,
          pixelSize,
          resolutionInMeters,
          showLineLabels,
          maxZoom,
          indications,
          elementCachesRef.current
        );
        // eslint-disable-next-line functional/immutable-data
        elementCachesRef.current = caches;
        return controls;
      }, [
        imageWithAnnotations.imageAndQuery.image.id,
        visibleAnnotations,
        allAnnotations,
        alwaysVisibleAnnotationIds,
        annotationOpacity,
        isAnnotationInProgress,
        indications
      ])
    : loadAnnotationControls(
        dispatch,
        visibleAnnotations,
        allAnnotations,
        alwaysVisibleAnnotationIds,
        fragmentLookup,
        aiFragmentLookup,
        userCanDeleteAnnotations,
        scaleFactor,
        annotationOpacity,
        isAnnotationInProgress,
        pixelSize,
        resolutionInMeters,
        showLineLabels,
        maxZoom,
        indications,
        // `?disableCache=true` means "disable all caching", so this branch always starts from
        // empty rather than reading `elementCachesRef` -- otherwise the per-annotation cache added
        // here would keep working even with the flag on, defeating the point of the comparison.
        EMPTY_ELEMENT_CACHES
      ).controls;

  function isHpfCursorVisible(): boolean {
    return hpfCursorVisible === true;
  }

  return (
    <>
      {/* This tile layer is to avoid empty tiles while tiles for z-stacked images or other zoom levels are loading (since there is this tile layer fixed to the min zoom tiles, this tile layer is shown as the "background" tiles instead of empty tiles which are gray boxes) */}
      <TileLayer
        url={tileUrl}
        zIndex={100}
        noWrap={true}
        minZoom={minZoom}
        maxNativeZoom={minZoom}
        eventHandlers={eventHandlers}
      />
      <TileLayer
        url={tileUrl}
        zIndex={101}
        noWrap={true}
        maxZoom={maxZoom}
        eventHandlers={{
          error: throttle(() => {
            // A tile has errored. Unfortunately, the HTTP response is not available at this stage
            // (see https://github.com/Leaflet/Leaflet/issues/5437#issuecomment-291863493). That
            // said, this _should_ only happen if the user's session has timed out. However, we
            // don't want to redirect if there's a different problem with the tile server. Thus, we
            // re-fetch the user to trigger a redirect if appropriate.
            dispatch(checkLoggedIn());
          }, 2000)
        }}
      />
      {/* These TileLayers are for preloading z-stack images. We switch between them when scrolling between z-stacked images. */}
      {zStackTileUrls.map(([index, url]) => (
        <TileLayer
          key={url}
          url={url}
          eventHandlers={eventHandlers}
          zIndex={index}
          // The Leaflet types are off here, this `zStackIndex` goes into the options on the resulting layer
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          zStackIndex={index}
          noWrap={true}
          minZoom={maxZoom}
          maxZoom={maxZoom}
        />
      ))}
      {/* NOTE: Order of annotation types matters here. The annotation types that should show up
          on the "bottom" -- the ones that other annotations, eg. points, can be drawn on top of --
          should be added to the map first. */}
      {"resource" in loggedInUser &&
        loggedInUser.resource.can([RolePermissions.AP_ImageCaseViewer_ViewAnnotationstab]) && (
          <>
            {cachedAnnotations.freehandAnnotationControls}
            {cachedAnnotations.hpfAnnotationControls}
            {cachedAnnotations.ellipseAnnotationControls}
            {cachedAnnotations.lineAnnotationControls}
            {cachedAnnotations.multiLineAnnotations.map(multiLine => {
              const multilineAnnotationClass = {
                id: multiLine.annotationClassId,
                name: multiLine.annotationClassName
              } as MultilineAnnotationClass;
              return (
                <MultilineAnnotation
                  key={multiLine.id}
                  positions={multiLine.geometry.coordinates.map(p => ({ lat: p[0], lng: p[1] }))}
                  annotationId={multiLine.id}
                  annotationClasses={
                    processedImageWithAnnotations.multilineAnnotationClassesWithCount
                  }
                  currentAnnotationClass={multilineAnnotationClass}
                  color={multiLine.color || undefined}
                  multilineCategory={multiLine.multilineCategory as MultilineCategory}
                  mapElement={drawMap}
                  canDelete={userCanDeleteAnnotations}
                  dispatch={dispatch}
                />
              );
            })}
            {cachedAnnotations.textAnnotationControls}
            {cachedAnnotations.pointAnnotationControls}
            {isHpfCursorVisible() ? (
              hpfCursor.hold != null && typeof hpfCursor.hold !== "undefined" ? (
                <>
                  <HPFAnnotation
                    key={"hpf-temp"}
                    text={"just a temp hpf"}
                    date={"no-data"}
                    position={hpfCursor.hold || { lat: 0, lng: 0 }}
                    radius={hpfRadius}
                    annotationId={"hpf-cursor-id"}
                    color={hpfCursorColor}
                    isClickable={false}
                    weight={5}
                    dashArray={""}
                    canDelete={false}
                    opacity={annotationOpacity}
                    source={"CR"}
                    dispatch={dispatch}
                  />
                </>
              ) : (
                <>
                  <HPFAnnotation
                    key={"hpf-cursor: " + (hpfCursorColor || "")}
                    text={HPF_CURSOR}
                    date={"no-data"}
                    position={hpfCursor.latlng || { lat: 0, lng: 0 }}
                    radius={hpfRadius}
                    annotationId={"hpf-cursor-id"}
                    color={hpfCursorColor}
                    isClickable={false}
                    weight={4}
                    dashArray={"5, 10"}
                    canDelete={false}
                    opacity={annotationOpacity}
                    source={"CR"}
                    dispatch={dispatch}
                  />
                </>
              )
            ) : (
              <></>
            )}
          </>
        )}
    </>
  );
};

export default ImageMap;

// caching

interface CachedAnnotations {
  pointAnnotationControls: ReadonlyArray<JSX.Element>;
  hpfAnnotationControls: ReadonlyArray<JSX.Element>;
  freehandAnnotationControls: ReadonlyArray<JSX.Element>;
  textAnnotationControls: ReadonlyArray<JSX.Element>;
  ellipseAnnotationControls: ReadonlyArray<JSX.Element>;
  lineAnnotationControls: ReadonlyArray<JSX.Element>;
  multiLineAnnotations: ReadonlyArray<MultiLineAnnotationType>;
}

// encapsulated annotation logic so we can memioze the result
// to improve performance during component re-renders
//
// Each annotation type is rendered through `cachedMap` rather than a plain `.map`, keyed by
// annotation id, so that an annotation whose relevant props haven't changed since the last call
// reuses the same element -- letting React skip its subtree instead of reconciling (and Leaflet
// re-mounting) every annotation whenever anything unrelated triggers a rebuild, e.g. toggling in
// or out of annotation-drawing mode.
//
// Each dep array must list every annotation field its element reads (geometry and createdAt
// included). Annotations are currently create/delete-only, so a given id's geometry never changes;
// if annotation editing is added, these deps are what make an edited annotation re-render.
//
// `visibleAnnotations` (the viewport-culled subset) is what actually gets mounted for every type
// except multiline, which is read from `allAnnotations` instead -- multiline popups are opened
// programmatically from the Celiac table rather than by clicking the layer, which only works while
// the layer stays mounted, so multiline can never be culled. `allAnnotations` is also used (but not
// mounted from) for the Celiac point-inside-freehand test, since a point's visibility must not
// depend on whether its containing freehand happens to be culled. `fragmentLookup`/`aiFragmentLookup`
// are similarly built from the full (uncull) tree elsewhere, so a point's class/colour is already
// known correctly the moment it scrolls into view.
function loadAnnotationControls(
  dispatch: AppDispatch,
  visibleAnnotations: ReadonlyArray<Annotation>,
  allAnnotations: ReadonlyArray<Annotation>,
  highlightedIds: ReadonlySet<string>,
  fragmentLookup: FragmentLookup,
  aiFragmentLookup: FragmentLookup,
  userCanDeleteAnnotations: boolean,
  scaleFactor: number,
  annotationOpacity: number | null,
  isAnnotationInProgress: boolean,
  pixelSize: number,
  resolutionInMeters: number,
  showLineLabels: boolean,
  maxZoom: number,
  indications: ReadonlyArray<Indication>,
  caches: AnnotationElementCaches
): { readonly controls: CachedAnnotations; readonly caches: AnnotationElementCaches } {
  const freehandAnnotations: ReadonlyArray<FreehandAnnotationType> = visibleAnnotations
    ? visibleAnnotations.filter(
        (annotation): annotation is FreehandAnnotationType => annotation.geometry.type === "Polygon"
      )
    : [];

  const freehandResult = cachedMap(
    caches.freehand,
    freehandAnnotations,
    freehandAnnotation => freehandAnnotation.id,
    freehandAnnotation => [
      freehandAnnotation.color,
      isAnnotationInProgress,
      userCanDeleteAnnotations,
      freehandAnnotation.annotationClassId,
      freehandAnnotation.source,
      freehandAnnotation.geometry,
      freehandAnnotation.createdAt
    ],
    freehandAnnotation => (
      <FreehandAnnotation
        key={freehandAnnotation.id}
        date={formatDate(freehandAnnotation.createdAt)}
        positions={freehandAnnotation.geometry.coordinates}
        annotationId={freehandAnnotation.id}
        color={freehandAnnotation.color}
        isClickable={!isAnnotationInProgress}
        canDelete={userCanDeleteAnnotations}
        freehandClassId={freehandAnnotation.annotationClassId}
        source={freehandAnnotation.source}
        dispatch={dispatch}
      />
    )
  );

  const pointAnnotations: ReadonlyArray<PointAnnotationType> = visibleAnnotations
    ? visibleAnnotations.filter(
        (annotation): annotation is PointAnnotationType =>
          annotation.geometry.type === "Point" && !("text" in annotation)
      )
    : [];

  const pointClassesFor = (point: PointAnnotationType) =>
    (point.source == "CR" ? fragmentLookup : aiFragmentLookup).pointClasses.get(point.id);

  // Computed once for the whole point set rather than per-point (as it used to be, inside
  // PointAnnotation itself): filtering all annotations down to freehands for every single point is
  // O(points x annotations), which dominates a rebuild once there are thousands of points.
  //
  // Reads from `allAnnotations`, not the culled `freehandAnnotations`: this is the second "must not
  // be culled" exception (after multiline). A point that is mounted -- e.g. force-mounted because
  // it's highlighted in the sidebar -- can sit inside a freehand that is culled, and testing against
  // only the mounted freehands would wrongly report it as outside, flipping its visibility.
  const isCeliacIndication = indications.includes(Indication.CeliacDisease);
  const allFreehandAnnotations: ReadonlyArray<FreehandAnnotationType> = isCeliacIndication
    ? allAnnotations.filter(
        (annotation): annotation is FreehandAnnotationType => annotation.geometry.type === "Polygon"
      )
    : [];
  const isPointInsideFreehand = (point: PointAnnotationType): boolean =>
    isCeliacIndication &&
    allFreehandAnnotations.some(freehand =>
      isPointInPolygon(
        convertCoordinatesToLatLngLiteral(point.geometry.coordinates),
        freehand.geometry
      )
    );

  const pointResult = cachedMap(
    caches.point,
    pointAnnotations,
    point => point.id,
    point => {
      const classes = pointClassesFor(point);
      return [
        point.color,
        annotationWeight(highlightedIds, point.id),
        userCanDeleteAnnotations,
        scaleFactor,
        classes?.hpfId,
        classes?.pointClassId,
        classes?.hpfClassId,
        point.source == "CR" ? null : annotationOpacity,
        point.source,
        isPointInsideFreehand(point),
        point.geometry.coordinates[0],
        point.geometry.coordinates[1],
        point.createdAt
      ];
    },
    point => {
      const classes = pointClassesFor(point);
      return (
        <PointAnnotation
          key={point.id}
          date={formatDate(point.createdAt)}
          position={convertCoordinatesToLatLngLiteral(point.geometry.coordinates)}
          annotationId={point.id}
          color={point.color}
          weight={annotationWeight(highlightedIds, point.id)}
          canDelete={userCanDeleteAnnotations}
          scaleFactor={scaleFactor}
          hpfId={classes?.hpfId}
          pointClassId={classes?.pointClassId}
          hpfClassId={classes?.hpfClassId}
          opacity={point.source == "CR" ? null : annotationOpacity}
          _source={point.source}
          isInsideFreehand={isPointInsideFreehand(point)}
          dispatch={dispatch}
        />
      );
    }
  );

  // hpf
  const hpfAnnotations: ReadonlyArray<HPFAnnotationType> = visibleAnnotations
    ? visibleAnnotations.filter(
        (annotation): annotation is HPFAnnotationType => "radius" in annotation
      )
    : [];

  const hpfClassIdFor = (hpf: HPFAnnotationType) =>
    (hpf.source === "CR" ? fragmentLookup : aiFragmentLookup).hpfClassIds.get(hpf.id);

  const hpfResult = cachedMap(
    caches.hpf,
    hpfAnnotations,
    hpf => hpf.id,
    hpf => [
      hpf.color,
      isAnnotationInProgress,
      annotationWeight(highlightedIds, hpf.id),
      hpfClassIdFor(hpf),
      userCanDeleteAnnotations,
      hpf.source == "CR" ? null : annotationOpacity,
      hpf.source,
      hpf.text,
      hpf.radius,
      hpf.geometry.coordinates[0],
      hpf.geometry.coordinates[1],
      hpf.createdAt
    ],
    hpf => (
      <HPFAnnotation
        key={hpf.id}
        text={hpf.text}
        date={formatDate(hpf.createdAt)}
        position={convertCoordinatesToLatLngLiteral(hpf.geometry.coordinates)}
        radius={hpf.radius}
        annotationId={hpf.id}
        color={hpf.color}
        isClickable={!isAnnotationInProgress}
        weight={annotationWeight(highlightedIds, hpf.id)}
        dashArray=""
        hpfClassId={hpfClassIdFor(hpf)}
        canDelete={userCanDeleteAnnotations}
        opacity={hpf.source == "CR" ? null : annotationOpacity}
        source={hpf.source}
        dispatch={dispatch}
      />
    )
  );

  const textAnnotationTypes: ReadonlyArray<TextAnnotationType> = visibleAnnotations
    ? visibleAnnotations.filter(
        (annotation): annotation is TextAnnotationType =>
          "text" in annotation && !("tilt" in annotation) && !("radius" in annotation)
      )
    : [];

  const textResult = cachedMap(
    caches.text,
    textAnnotationTypes,
    marker => marker.id,
    marker => [
      marker.text,
      userCanDeleteAnnotations,
      marker.geometry.coordinates[0],
      marker.geometry.coordinates[1],
      marker.createdAt
    ],
    marker => (
      <TextMarker
        key={marker.id}
        text={marker.text}
        date={formatDate(marker.createdAt)}
        position={convertCoordinatesToLatLngLiteral(marker.geometry.coordinates)}
        annotationId={marker.id}
        canDelete={userCanDeleteAnnotations}
        dispatch={dispatch}
      />
    )
  );

  const ellipseAnnotationTypes: ReadonlyArray<EllipseAnnotationType> = visibleAnnotations
    ? visibleAnnotations.filter(
        (annotation): annotation is EllipseAnnotationType => "tilt" in annotation
      )
    : [];

  const ellipseResult = cachedMap(
    caches.ellipse,
    ellipseAnnotationTypes,
    ellipse => ellipse.id,
    ellipse => [
      ellipse.text,
      isAnnotationInProgress,
      userCanDeleteAnnotations,
      ellipse.radiusX,
      ellipse.radiusY,
      ellipse.tilt,
      ellipse.geometry.coordinates[0],
      ellipse.geometry.coordinates[1],
      ellipse.createdAt
    ],
    ellipse => (
      <EllipseAnnotationWithPopup
        key={ellipse.id}
        text={ellipse.text}
        date={formatDate(ellipse.createdAt)}
        position={convertCoordinatesToLatLngLiteral(ellipse.geometry.coordinates)}
        radii={[ellipse.radiusX, ellipse.radiusY]}
        tilt={ellipse.tilt}
        annotationId={ellipse.id}
        isClickable={!isAnnotationInProgress}
        canDelete={userCanDeleteAnnotations}
        dispatch={dispatch}
      />
    )
  );

  const lineAnnotationTypes: ReadonlyArray<LineAnnotationType> = visibleAnnotations
    ? visibleAnnotations.filter(
        (annotation): annotation is LineAnnotationType =>
          annotation.annotationType === AnnotationType.Line
      )
    : [];

  const lineResult = cachedMap(
    caches.line,
    lineAnnotationTypes,
    line => line.id,
    line => [
      line.length,
      pixelSize,
      resolutionInMeters,
      showLineLabels,
      maxZoom,
      userCanDeleteAnnotations,
      line.geometry,
      line.createdAt
    ],
    line => {
      const micrometers =
        pixelSize && line.length ? toMicrometers(line.length, pixelSize, resolutionInMeters) : 0;
      return (
        <LineAnnotation
          key={line.id}
          date={formatDate(line.createdAt)}
          positions={line.geometry.coordinates.map(p => ({ lat: p[0], lng: p[1] }))}
          text={micrometers ? formatMicrometers(micrometers) : ""}
          showLabel={showLineLabels}
          lineLength={micrometers}
          annotationId={line.id}
          canDelete={userCanDeleteAnnotations}
          maxZoom={maxZoom}
          dispatch={dispatch}
        />
      );
    }
  );

  const multiLineAnnotations: ReadonlyArray<MultiLineAnnotationType> = allAnnotations
    ? allAnnotations.filter(
        (annotation): annotation is MultiLineAnnotationType =>
          annotation.annotationType === AnnotationType.Multiline
      )
    : [];

  return {
    controls: {
      pointAnnotationControls: pointResult.values,
      hpfAnnotationControls: hpfResult.values,
      freehandAnnotationControls: freehandResult.values,
      textAnnotationControls: textResult.values,
      ellipseAnnotationControls: ellipseResult.values,
      lineAnnotationControls: lineResult.values,
      multiLineAnnotations: multiLineAnnotations
    },
    caches: {
      point: pointResult.cache,
      hpf: hpfResult.cache,
      freehand: freehandResult.cache,
      text: textResult.cache,
      ellipse: ellipseResult.cache,
      line: lineResult.cache
    }
  };
}
